import { observable, computed, toJS } from "mobx";
import * as mobx from "mobx";
import _ from "lodash";
import moment from "moment";
import firebase from "firebase";

import blockStore from "../canvas/store";
import { linkProps, resolveRules } from "./linker";
import { clock, countdown } from "./providers/clock";
import twitch from "./providers/twitch";
import strawpoll from "./providers/strawpoll";
import twitter, { feed } from "./providers/twitter";
import streamlabs from "./providers/streamlabs";
import mindcrack from "./providers/mindcrack";
import { ItemTypes } from "../constants/elements";
import { orderList } from "../reusable/utils";
import { blockState } from "../scheduler/index";

import imagePlaceholder from "../reusable/images/ImagePlaceholder.svg";

export const Providers = {
  Twitch: "twitch",
  Countdown: "countdown",
  Clock: "clock",
  Strawpoll: "strawpoll",
  Twitter: "twitter",
  Streamlabs: "streamlabs",
  Mindcrack: "mindcrack",
  TwitterFeed: "twitterfeed"
};

export const tokens = observable.map({});

const store = observable.map({
  [Providers.Twitch]: {
    type: Providers.Twitch
  },
  clock: {
    type: Providers.Clock,
    props: {}
  },
  countdown: {
    type: Providers.Countdown,
    props: {
      duration: { type: "text", value: "" },
      target: {
        type: "time",
        value: moment().add(1, "hour")
      },
      startAt: {
        type: "time",
        value: moment().add(30, "minute")
      }
    }
  },
  strawpoll: {
    type: Providers.Strawpoll,
    props: {
      id: { type: "text", value: "12899492" }
    }
  },
  twitter: {
    type: Providers.Twitter,
    props: {
      tweetId: { type: "text", value: "863101170444906497" }
    }
  },
  [Providers.Streamlabs]: {
    type: Providers.Streamlabs,
    props: {}
  },
  [Providers.TwitterFeed]: {
    type: Providers.TwitterFeed,
    props: {
      keyword: { type: "text", value: "#twitch" }
    }
  }
});

const customProviders = {
  //"twitch:89127233": "mindcrack"
  // "twitch:176997821": "mindcrack"
};

var userId = undefined;

export const dbJS = computed(() => {
  return toJS(store);
});

const providers = {
  [Providers.Twitch]: twitch,
  [Providers.Countdown]: countdown,
  [Providers.Clock]: clock,
  [Providers.Strawpoll]: strawpoll,
  [Providers.Twitter]: twitter,
  [Providers.Streamlabs]: streamlabs,
  [Providers.Mindcrack]: mindcrack,
  [Providers.TwitterFeed]: feed
};

export const updateCell = (id, prop, value) => {
  firebase
    .database()
    .ref("/spreadsheet/" + userId + "/" + id + "/props/" + prop + "/value")
    .set(value);
};

export const addEntity = (id, props) => {
  id.length > 0 &&
    firebase
      .database()
      .ref("/spreadsheet/" + userId + "/" + id + "/props/")
      .set(props || true);
};

export const removeEntity = id => {
  firebase
    .database()
    .ref("/spreadsheet/" + userId + "/" + id)
    .remove();
};

export const createCell = (id, prop, type) => {
  const defaults = {
    time: "12:00",
    image: imagePlaceholder,
    text: "Some text",
    link: "Chose link"
  };
  const v = defaults[type] && { value: defaults[type] };
  firebase
    .database()
    .ref("/spreadsheet/" + userId + "/" + id + "/props/" + prop)
    .set({ ...v, type: type });
};

var memory = {};

const checkMemory = (entityId, extra) => {
  const diff = _.difference(memory[entityId], Object.keys(extra));
  memory[entityId] = Object.keys(extra);
  diff.forEach(e => store.delete(e));
};

const setDerived = mobx.action("setDerived", (derived, entityId, extra) => {
  store.get(entityId).props = {
    ...store.get(entityId).props,
    ..._.mapValues(derived, d => ({ ...d, readOnly: true }))
  };
  extra && store.merge(extra);
  extra && checkMemory(entityId, extra);
});

export const setEntityLocally = (id, data) => {
  setDerived(data, id);
};

const getData = (entityId, type, props) => {
  providers[type](
    { ..._.mapValues(props, p => p.value), userId: userId, _tokens: toJS(tokens) },
    entityId,
    (derived, extra) => {
      setDerived(derived, entityId, extra);
    }
  );
};

const getDatas = tokens => store.forEach((v, k) => v.type && getData(k, v.type, v.props, tokens));

const flattenEntity = (id, props, spread, pullFn) => {
  return _.reduce(
    props,
    (acc, v, k) => {
      return {
        ...acc,
        ...(v.type === "link" && spread[v.value]
          ? flattenEntity(id + "." + k, spread[v.value].props, spread, pullFn)
          : v.type === "list"
            ? {
                [id + "." + k]: pullFn({
                  type: v.type,
                  value: _.map(orderList(v.value), item => {
                    return (
                      (spread[item] &&
                        flattenEntity(
                          id + "." + k,
                          { ...spread[item].props, __id: { value: item, type: "text" } },
                          spread,
                          pullFn
                        )) ||
                      {}
                    );
                  })
                })
              }
            : { [id + "." + k]: pullFn(v) })
      };
    },
    {}
  );
};

export const flattenSpreadsheet = (spread, pull) => {
  const pullFn = pull || (v => v.value);
  return _.reduce(
    spread,
    (acc, v, k) => ({ ...acc, ...flattenEntity(k, v.props, spread, pullFn) }),
    {}
  );
};

export const flattenDBInfo = computed(() => flattenSpreadsheet(toJS(store), v => v));

export const getCollections = computed(() => {
  const fs = flattenDBInfo.get();
  return Object.keys(fs).filter(c => fs[c].type === "list");
});

export const flattenDB = computed(() => flattenSpreadsheet(toJS(store)));

const resolveListFields = (listId, flattenInfo) => {
  const keys = flattenInfo[listId].value[0];
  return keys ? Object.keys(keys) : [];
};

export const linkableFields = computed(() => {
  const flatten = flattenDBInfo.get();
  const listFields = Object.keys(flatten).reduce(
    (acc, c) => (flatten[c].type === "list" ? acc.concat(resolveListFields(c, flatten)) : acc),
    []
  );
  return Object.keys(flatten).concat(listFields);
});

const linkCollData = (blocks, db) => {
  return _.mapValues(blocks, (v, k) => {
    return v.collection && v.collection !== "none"
      ? {
          ...v,
          collectionData: db[v.collection]
          // &&
          // _.chain(db[v.collection])
          //   .map((i, idx) => ({ ...i, [v.collection + "._position"]: idx + 1 }))
          //   .drop(v.skip) //.drop(v.cycle ? v._skip % db[v.collection].length : v.skip)
          //   .take(v.limit)
          //   .value()
        }
      : v;
  });
};

export const linkBlocks = blocks => {
  const flat = flattenDB.get();
  // const withColl = linkCollections(blockStore.blocksJS, flat);
  //const withColl = resolveCollections(toJS(blocks), flat, "canvas");
  const withColl = linkCollData(toJS(blocks), flat);
  const withLocal = _.mapValues(withColl, v => ({
    ...v,
    ...blockState.get(v.id),
    collectionData:
      (blockState.get(v.id) && blockState.get(v.id).liveCollectionData) || v.collectionData
  }));
  const withLink = _.mapValues(withLocal, (v, k) => linkProps(v, flat));
  const withRules = resolveRules(withLink, flat);
  return withRules;
};

export const linkedBlocks = computed(() => {
  return linkBlocks(blockStore.blocks);
});

const updateStore = mobx.action("resetStore", val => {
  store.merge(_.merge(dbJS.get(), val));
  const currentCustomKeys = Object.keys(dbJS.get()).filter(
    x => !Object.values(Providers).includes(x) && !store.get(x).hidden
  );
  _.difference(currentCustomKeys, Object.keys(val)).forEach(e => {
    store.delete(e);
  });
});

export const initDB = uid => {
  _.map(customProviders, (v, k) => {
    if (k !== uid) {
      store.delete(v);
    }
  });
  userId = uid;
  firebase
    .database()
    .ref("/spreadsheet/" + userId)
    .on("value", snap => {
      if (snap.val()) {
        updateStore(snap.val());
      }

      getDatas();
    });
  firebase
    .database()
    .ref("/tokens/" + userId)
    .on("value", snap => {
      if (snap.val()) {
        tokens.merge(snap.val());
        getDatas();
      }
    });
};

export const saveTokens = (provider, access, refresh) => {
  firebase
    .database()
    .ref(`/tokens/${userId}/${provider}`)
    .set({ access_token: access });
};

export const PropTypeToElem = {
  time: ItemTypes.ELEMENT_TEXT,
  number: ItemTypes.ELEMENT_TEXT,
  image: ItemTypes.ELEMENT_IMAGE,
  text: ItemTypes.ELEMENT_TEXT,
  list: ItemTypes.ELEMENT_EMPTYCONTAINER,
  progress: ItemTypes.ELEMENT_PROGRESSBAR
};

export default store;



// WEBPACK FOOTER //
// ./src/spreadsheet/store.js